import apiClient from './apiClient';
import { logger } from '../utils/logger'
import {
  Invoice, 
  InvoiceFilters, 
  InvoiceStats, 
  CreateInvoiceRequest 
} from '../types/financial.types';

class UserInvoiceService {
  private baseURL = '/user-invoices';

  /**
   * Obter lista de faturas com filtros para usuários
   */
  async getInvoices(filters: InvoiceFilters = {}): Promise<{
    invoices: Invoice[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      total_pages: number;
    };
  }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.payment_method && filters.payment_method !== 'all') {
        params.append('payment_method', filters.payment_method);
      }
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type);
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.page) {
        params.append('page', filters.page.toString());
      }
      if (filters.limit) {
        params.append('limit', filters.limit.toString());
      }

      const response = await apiClient.get(`${this.baseURL}?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      logger.error('Erro ao obter faturas:', error);
      throw error;
    }
  }

  /**
   * Obter estatísticas de faturas para usuários
   */
  async getStats(): Promise<InvoiceStats> {
    try {
      const response = await apiClient.get(`${this.baseURL}/stats`);
      return response.data.data;
    } catch (error: any) {
      logger.error('Erro ao obter estatísticas de faturas:', error);
      throw error;
    }
  }

  /**
   * Obter fatura por ID para usuários
   */
  async getInvoiceById(id: string): Promise<Invoice> {
    try {
      const response = await apiClient.get(`${this.baseURL}/${id}`);
      return response.data.data; // Acessar o campo 'data' dentro da resposta
    } catch (error: any) {
      logger.error('Erro ao obter fatura:', error);
      throw error;
    }
  }

  /**
   * Criar nova fatura para usuários (apenas para clientes)
   */
  async createInvoice(invoiceData: CreateInvoiceRequest): Promise<Invoice> {
    try {
      const response = await apiClient.post(this.baseURL, invoiceData);
      return response.data;
    } catch (error: any) {
      logger.error('Erro ao criar fatura:', error);
      throw error;
    }
  }

  /**
   * Processar pagamento transparente para usuários
   */
  async processTransparentPayment(id: string, paymentData: any): Promise<any> {
    try {
      const response = await apiClient.post(`${this.baseURL}/${id}/transparent-payment`, paymentData);
      return response.data;
    } catch (error: any) {
      logger.error('Erro ao processar pagamento:', error);
      throw error;
    }
  }

  /**
   * Gerar PDF da fatura para usuários
   */
  async generatePDF(id: string): Promise<Blob> {
    try {
      const response = await apiClient.get(`${this.baseURL}/${id}/pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      logger.error('Erro ao gerar PDF:', error);
      throw error;
    }
  }

  /**
   * Exportar faturas para usuários
   */
  async exportInvoices(filters: InvoiceFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        params.append('status', filters.status);
      }
      if (filters.payment_method && filters.payment_method !== 'all') {
        params.append('payment_method', filters.payment_method);
      }
      if (filters.type && filters.type !== 'all') {
        params.append('type', filters.type);
      }
      if (filters.date_from) {
        params.append('date_from', filters.date_from);
      }
      if (filters.date_to) {
        params.append('date_to', filters.date_to);
      }
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await apiClient.get(`${this.baseURL}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error: any) {
      logger.error('Erro ao exportar faturas:', error);
      throw error;
    }
  }
}

export const userInvoiceService = new UserInvoiceService();
